import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FiCheck, FiCopy, FiLink, FiLoader, FiMail, FiSend } from "react-icons/fi";
import AccountShell from "./AccountShell";
import {
  buildReferralUrl,
  getAuthDisplayName,
  getAuthReferralCode,
  getAuthToken,
} from "../../utils/userIdentity";
import {
  REFERRAL_COPY,
  sendReferralInvite,
} from "../../utils/sendReferral";
import { scrollTop } from "../../utils/window";

function ReferralPage({ auth }) {
  const referralUrl = useMemo(() => buildReferralUrl(auth), [auth]);
  const referralCode = useMemo(() => getAuthReferralCode(auth), [auth]);
  const token = getAuthToken(auth);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    scrollTop();
  }, []);

  const copyUrl = async () => {
    if (!referralUrl) return;
    setMessage(null);
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setMessage({
        type: "ok",
        source: "copy",
        text: referralCode ? REFERRAL_COPY.copied : REFERRAL_COPY.copiedNoCode,
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
      setMessage({
        type: "err",
        source: "copy",
        text: "Could not copy the link. Select it and copy manually.",
      });
    }
  };

  const sendInvite = async (event) => {
    event.preventDefault();
    setMessage(null);
    setSending(true);
    try {
      const result = await sendReferralInvite({ email: inviteEmail, token });
      setInviteEmail("");
      setMessage({ type: "ok", source: "invite", text: result.message });
    } catch (error) {
      setMessage({
        type: "err",
        source: "invite",
        text: error?.message || REFERRAL_COPY.emailFailed,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <AccountShell
      eyebrow="Invite"
      title="Invite friends"
      lead="Copy your personal signup link, or send it by email. Credits go to both wallets when they sign up."
    >
      <p className="pi-account-note" style={{ marginBottom: "1.1rem" }}>
        Signed in as <strong>{getAuthDisplayName(auth)}</strong>
        {referralCode ? (
          <>
            {" "}
            · code <strong>{referralCode}</strong>
          </>
        ) : (
          <> — no referral code on this account yet.</>
        )}
      </p>

      <section className="pi-referral-card">
        <div className="pi-referral-card__icon" aria-hidden>
          <FiLink size={18} />
        </div>
        <div className="pi-referral-card__body">
          <h2>Your referral link</h2>
          <p>
            {referralCode
              ? REFERRAL_COPY.linkWithCode
              : REFERRAL_COPY.linkWithoutCode}
          </p>
          {message?.source === "copy" ? (
            <p
              className={`pi-account-alert pi-account-alert--${
                message.type === "ok" ? "ok" : "err"
              }`}
              style={{ marginBottom: "0.85rem" }}
            >
              {message.text}
            </p>
          ) : null}
          {!referralCode ? (
            <p className="pi-account-note" style={{ marginBottom: "0.85rem" }}>
              {REFERRAL_COPY.noCode}
            </p>
          ) : null}
          <div className="pi-account-copy-row">
            <input id="referral-url" readOnly value={referralUrl} aria-label="Referral URL" />
            <button
              type="button"
              className="pi-account-icon-btn"
              disabled={!referralUrl}
              onClick={copyUrl}
              aria-label={copied ? "Copied" : "Copy referral URL"}
              title={copied ? "Copied" : "Copy"}
            >
              {copied ? <FiCheck size={18} aria-hidden /> : <FiCopy size={18} aria-hidden />}
            </button>
          </div>
        </div>
      </section>

      <section className="pi-referral-card pi-referral-card--invite">
        <div className="pi-referral-card__icon" aria-hidden>
          <FiMail size={18} />
        </div>
        <div className="pi-referral-card__body">
          <h2>Send by email</h2>
          <p>{REFERRAL_COPY.emailHint}</p>
          {message?.source === "invite" ? (
            <p
              className={`pi-account-alert pi-account-alert--${
                message.type === "ok" ? "ok" : "err"
              }`}
              style={{ marginBottom: "0.85rem" }}
            >
              {message.text}
            </p>
          ) : null}
          <form className="pi-account-copy-row" onSubmit={sendInvite}>
            <input
              id="referral-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="name@email.com"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              aria-label="Friend’s email"
              required
              disabled={sending}
            />
            <button
              type="submit"
              className="pi-account-icon-btn"
              disabled={sending || !inviteEmail.trim()}
              aria-label={sending ? "Sending" : "Send invite"}
              title={sending ? "Sending…" : "Send"}
            >
              {sending ? (
                <FiLoader size={18} className="pi-spin" aria-hidden />
              ) : (
                <FiSend size={18} aria-hidden />
              )}
            </button>
          </form>
        </div>
      </section>

      <p className="pi-account-note" style={{ marginTop: "1rem" }}>
        Learn how referral credits work.{" "}
        <Link to="/dostprogram">Open Referral</Link>
      </p>
    </AccountShell>
  );
}

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps)(ReferralPage);
